(() => {
  const PACKING_CONFIG_SELECTOR = '[data-packing-fee-config]';
  const EXTRA_CHARGE_CONFIG_SELECTOR = '[data-extra-charge-config]';
  const PALLET_INPUT_SELECTOR = 'input[name="properties[Paller]"]';
  const QUANTITY_INPUT_SELECTOR = '.qty-input__input, input[name="quantity"]';

  function toNumber(value) {
    if (typeof value === 'number') return Number.isFinite(value) ? value : 0;

    const normalized = String(value || '').replace(',', '.').trim();
    const parsed = Number.parseFloat(normalized);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  function formatMoney(cents) {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(cents / 100);
  }

  function formatWholeMoney(cents) {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(cents / 100);
  }

  function formatPallets(value) {
    return new Intl.NumberFormat('de-DE', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  }

  function createChargeGroupId(prefix) {
    if (window.crypto?.randomUUID) return `${prefix}-${window.crypto.randomUUID()}`;

    return `${prefix}-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
  }

  function hasFractionalPart(value) {
    if (!Number.isFinite(value)) return false;
    return Math.abs(value - Math.round(value)) > 0.000001;
  }

  function getChargeField(form, type, fieldName) {
    return form.querySelector(`[data-${type}-field="${fieldName}"]`);
  }

  function getPalletCount(form) {
    const palletInput = form.querySelector(PALLET_INPUT_SELECTOR);
    return palletInput ? Math.max(0, toNumber(palletInput.value)) : 0;
  }

  function getQuantityCount(form) {
    const quantityInput = form.querySelector(QUANTITY_INPUT_SELECTOR);
    return quantityInput ? Math.max(0, toNumber(quantityInput.value)) : 0;
  }

  function getPackingFeeBasis(form) {
    const basisField = getChargeField(form, 'packing-fee', 'basis');
    return basisField?.value === 'quantity' ? 'quantity' : 'pallets';
  }

  function updatePackingFeeDisplay(
    form,
    chargeBasis,
    basisValue,
    chargeablePalletCount,
    feePriceCents,
    feeTotalCents,
    maxChargePallets
  ) {
    const displayNode = form.querySelector('[data-packing-fee-display]');
    if (!displayNode) return;

    if (basisValue <= 0 || chargeablePalletCount <= 0 || feePriceCents <= 0) {
      displayNode.style.display = 'none';
      return;
    }

    displayNode.style.display = 'block';

    const unitPriceNode = displayNode.querySelector('[data-packing-fee-unit-price]');
    const qtyNode = displayNode.querySelector('[data-packing-fee-qty-display]');
    const totalNode = displayNode.querySelector('[data-packing-fee-total-display]');
    const palletsNode = displayNode.querySelector('[data-packing-fee-pallets-display]');
    const capNoteNode = displayNode.querySelector('[data-packing-fee-cap-note]');

    if (unitPriceNode) unitPriceNode.textContent = formatMoney(feePriceCents);
    if (qtyNode) qtyNode.textContent = String(chargeablePalletCount);
    if (totalNode) totalNode.textContent = formatWholeMoney(feeTotalCents);
    if (palletsNode) palletsNode.textContent = formatPallets(palletCount);
    if (capNoteNode) {
      const roundedPalletCount = Math.ceil(basisValue);
      capNoteNode.textContent = maxChargePallets > 0 && roundedPalletCount > chargeablePalletCount
        ? (chargeBasis === 'quantity'
          ? ` Max ${chargeablePalletCount} items charged.`
          : ` Max ${chargeablePalletCount} pallets charged.`)
        : '';
    }
  }

  function updateExtraChargeDisplay(
    form,
    palletCount,
    chargeablePalletCount,
    feePriceCents,
    feeTotalCents,
    maxChargePallets
  ) {
    const displayNode = form.querySelector('[data-extra-charge-display]');
    if (!displayNode) return;

    if (
      palletCount <= 0
      || chargeablePalletCount <= 0
      || feePriceCents <= 0
      || !hasFractionalPart(palletCount)
    ) {
      displayNode.style.display = 'none';
      return;
    }

    displayNode.style.display = 'block';

    const qtyNode = displayNode.querySelector('[data-extra-charge-qty-display]');
    const totalNode = displayNode.querySelector('[data-extra-charge-total-display]');
    const capNoteNode = displayNode.querySelector('[data-extra-charge-cap-note]');

    if (qtyNode) qtyNode.textContent = String(chargeablePalletCount);
    if (totalNode) totalNode.textContent = formatWholeMoney(feeTotalCents);
    if (capNoteNode) {
      const roundedPalletCount = Math.ceil(palletCount);
      capNoteNode.textContent = maxChargePallets > 0 && roundedPalletCount > chargeablePalletCount
        ? `Maks ${chargeablePalletCount} paller belastes.`
        : '';
    }
  }

  function syncPackingFeeForm(form, options = {}) {
    const { forceNewGroupId = false } = options;
    const priceField = getChargeField(form, 'packing-fee', 'price');
    if (!priceField) return;

    const quantity = getQuantityCount(form);
    const chargeBasis = getPackingFeeBasis(form);
    const maxPalletsField = getChargeField(form, 'packing-fee', 'max-pallets');
    const palletCount = getPalletCount(form);
    const basisValue = chargeBasis === 'quantity' ? quantity : palletCount;
    const roundedPalletCount = basisValue > 0 ? Math.ceil(basisValue) : 0;
    const maxChargePallets = maxPalletsField ? Math.max(0, Math.floor(toNumber(maxPalletsField.value))) : 0;
    const chargeablePalletCount = maxChargePallets > 0
      ? Math.min(roundedPalletCount, maxChargePallets)
      : roundedPalletCount;
    const palletRatio = chargeBasis === 'pallets'
      ? (quantity > 0 ? palletCount / quantity : 0)
      : (quantity > 0 ? basisValue / quantity : 0);
    const feePriceCents = Math.max(0, Math.round(toNumber(priceField.value)));
    const feeTotalCents = chargeablePalletCount * feePriceCents;
    const groupIdField = getChargeField(form, 'packing-fee', 'group-id');

    const palletsField = getChargeField(form, 'packing-fee', 'pallets');
    const qtyField = getChargeField(form, 'packing-fee', 'qty');
    const ratioField = getChargeField(form, 'packing-fee', 'ratio');
    const basisField = getChargeField(form, 'packing-fee', 'basis');
    const basisValueField = getChargeField(form, 'packing-fee', 'basis-value');

    if (palletsField) palletsField.value = basisValue.toFixed(4);
    if (qtyField) qtyField.value = String(chargeablePalletCount);
    if (ratioField) ratioField.value = palletRatio.toFixed(8);
    if (basisField) basisField.value = chargeBasis;
    if (basisValueField) basisValueField.value = basisValue.toFixed(4);

    if (groupIdField) {
      if (chargeablePalletCount > 0 && feePriceCents > 0) {
        if (forceNewGroupId || !groupIdField.value) {
          groupIdField.value = createChargeGroupId('packing-fee');
        }
      } else {
        groupIdField.value = '';
      }
    }

    form.dataset.packingFeeProductId = getChargeField(form, 'packing-fee', 'product-id')?.value || '';
    form.dataset.packingFeeVariantId = getChargeField(form, 'packing-fee', 'variant-id')?.value || '';
    form.dataset.packingFeePrice = String(feePriceCents);
    form.dataset.packingFeeQty = String(chargeablePalletCount);
    form.dataset.packingFeeGroupId = groupIdField?.value || '';

    updatePackingFeeDisplay(
      form,
      chargeBasis,
      basisValue,
      chargeablePalletCount,
      feePriceCents,
      feeTotalCents,
      maxChargePallets
    );
  }

  function syncExtraChargeForm(form, options = {}) {
    const { forceNewGroupId = false } = options;
    const priceField = getChargeField(form, 'extra-charge', 'price');
    if (!priceField) return;

    const maxPalletsField = getChargeField(form, 'extra-charge', 'max-pallets');
    const palletCount = getPalletCount(form);
    const roundedPalletCount = palletCount > 0 ? Math.ceil(palletCount) : 0;
    const hasExtraCharge = hasFractionalPart(palletCount);
    const maxChargePallets = maxPalletsField ? Math.max(0, Math.floor(toNumber(maxPalletsField.value))) : 0;
    const chargeablePalletCount = hasExtraCharge && maxChargePallets > 0
      ? Math.min(roundedPalletCount, maxChargePallets)
      : 0;
    const feePriceCents = Math.max(0, Math.round(toNumber(priceField.value)));
    const feeTotalCents = chargeablePalletCount * feePriceCents;
    const feeTotal = (feeTotalCents / 100).toFixed(2);
    const groupIdField = getChargeField(form, 'extra-charge', 'group-id');

    const palletsField = getChargeField(form, 'extra-charge', 'pallets');
    const qtyField = getChargeField(form, 'extra-charge', 'qty');
    const amountField = getChargeField(form, 'extra-charge', 'amount');

    if (palletsField) palletsField.value = palletCount.toFixed(4);
    if (qtyField) qtyField.value = String(chargeablePalletCount);
    if (amountField) amountField.value = hasExtraCharge ? feeTotal : '0.00';

    if (groupIdField) {
      if (hasExtraCharge && chargeablePalletCount > 0 && feePriceCents > 0) {
        if (forceNewGroupId || !groupIdField.value) {
          groupIdField.value = createChargeGroupId('extra-charge');
        }
      } else {
        groupIdField.value = '';
      }
    }

    form.dataset.extraChargeProductId = getChargeField(form, 'extra-charge', 'product-id')?.value || '';
    form.dataset.extraChargeVariantId = getChargeField(form, 'extra-charge', 'variant-id')?.value || '';
    form.dataset.extraChargePrice = String(feePriceCents);
    form.dataset.extraChargeQty = String(chargeablePalletCount);
    form.dataset.extraChargeGroupId = groupIdField?.value || '';

    updateExtraChargeDisplay(
      form,
      palletCount,
      chargeablePalletCount,
      feePriceCents,
      feeTotalCents,
      maxChargePallets
    );
  }

  function syncChargeForm(form, options = {}) {
    syncPackingFeeForm(form, options);
    syncExtraChargeForm(form, options);
  }

  function scheduleChargeFormSync(form) {
    if (!form) return;
    if (form.dataset.chargeSyncScheduled === 'true') return;

    form.dataset.chargeSyncScheduled = 'true';
    requestAnimationFrame(() => {
      form.dataset.chargeSyncScheduled = 'false';
      syncChargeForm(form);
    });
  }

  function registerChargeForm(form) {
    if (!form || form.dataset.chargeReady === 'true') return;

    form.dataset.chargeReady = 'true';

    const scheduleSync = () => {
      scheduleChargeFormSync(form);
    };

    form.addEventListener('click', scheduleSync);
    form.addEventListener('input', scheduleSync);
    form.addEventListener('change', scheduleSync);
    form.addEventListener('keyup', scheduleSync);
    form.addEventListener('submit', () => {
      syncChargeForm(form, { forceNewGroupId: true });
    }, true);

    syncChargeForm(form);
  }

  function registerConfig(configNode) {
    registerChargeForm(configNode.closest('form'));
  }

  function initChargeConfigs(root = document) {
    root.querySelectorAll(PACKING_CONFIG_SELECTOR).forEach(registerConfig);
    root.querySelectorAll(EXTRA_CHARGE_CONFIG_SELECTOR).forEach(registerConfig);
  }

  document.addEventListener('DOMContentLoaded', () => {
    initChargeConfigs();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initChargeConfigs(event.target);
  });

  document.addEventListener('on:variant:change', () => {
    const seenForms = new Set();

    document
      .querySelectorAll(`${PACKING_CONFIG_SELECTOR}, ${EXTRA_CHARGE_CONFIG_SELECTOR}`)
      .forEach((configNode) => {
        const form = configNode.closest('form');
        if (!form || seenForms.has(form)) return;

        seenForms.add(form);
        scheduleChargeFormSync(form);
      });
  });
})();
